import { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router';
import SocketIO, { Socket } from 'socket.io-client';
import { create } from 'zustand';
import { useStoreWithEqualityFn } from 'zustand/traditional';

import { useAuth } from '@/context/appContext';
import { useInterval } from '@/food-editor/components/HumanTime';
import { buildUrl } from '@/utils';

import { config } from '../config';

/**
 * Strip RxTable filter/sort (and similar) query noise from presence locations.
 * Without this, every foods/table filter keystroke emits presence:ident and the
 * server rebroadcasts presence:state to every client — the main remaining lag.
 * Keeps triage/scrape food names, region, and other meaningful params.
 */
export const normalizePresenceLocation = (location: string | null | undefined): string => {
  if (!location) {
    return '';
  }
  const [path, qs = ''] = location.split('?');
  if (!qs) {
    return path;
  }
  const params = new URLSearchParams(qs);
  const kept = new URLSearchParams();
  params.forEach((value, key) => {
    if (key.endsWith('-filter') || key.endsWith('-sort')) {
      return;
    }
    kept.set(key, value);
  });
  const next = kept.toString();
  return next ? `${path}?${next}` : path;
};

let lastPresenceIdentLocation = '';
let presenceIdentTimer: ReturnType<typeof setTimeout> | null = null;
let pendingPresenceIdentLocation: string | null = null;

/** Debounced ident emit; skips no-ops after {@link normalizePresenceLocation}. */
export const emitPresenceIdent = (location: string) => {
  const normalized = normalizePresenceLocation(location);
  pendingPresenceIdentLocation = normalized;
  if (presenceIdentTimer != null) {
    return;
  }
  presenceIdentTimer = setTimeout(() => {
    presenceIdentTimer = null;
    const next = pendingPresenceIdentLocation;
    pendingPresenceIdentLocation = null;
    if (next == null || next === lastPresenceIdentLocation) {
      return;
    }
    const { socket, connected } = useSocketIOClientStore.getState();
    if (!connected || !socket) {
      return;
    }
    lastPresenceIdentLocation = next;
    socket.emit('presence:ident', { location: next });
  }, 750);
};

const socketIOConnect = (opts: { authToken?: string; setConnected: (connected: boolean) => void }) => {
  const socket = SocketIO(config.NOTIFICATION_ENGINE_URL, {
    transports: ['websocket', 'polling'],
    auth: {
      token: opts.authToken,
    },
  });

  const updateConnected = () => opts.setConnected(socket.connected);
  socket.on('connect', updateConnected);
  socket.on('connect', (...args) => {
    console.log('SocketIO: connected!', args);
  });

  // High-frequency server chatter — logging every payload freezes DevTools on
  // busy pages (foods/table) without helping local debugging.
  const quietSocketEvents = new Set(['presence:state', 'pong', 'ping', 'queues:refetch']);

  socket.onAnyOutgoing((event, ...args) => {
    if (quietSocketEvents.has(event)) {
      return;
    }
    console.log('SocketIO -> server:', event, args);
  });

  socket.onAny((event, ...args) => {
    if (quietSocketEvents.has(event)) {
      return;
    }
    console.log('SocketIO <- server:', event, args);
  });

  socket.on('disconnect', updateConnected);
  socket.on('disconnect', (...args) => {
    console.log('SocketIO: disconnected!', args);
  });

  return socket;
};

const socketIODisconnect = (socket: Socket) => {
  socket.close();
  socket.off();
};

export type SocketIOClientStore = {
  socket: Socket | null;
  connected: boolean;
  telemetry: {
    rttTotal: number;
    rttSuccess: number;
    rttFail: number;
    rttLatest: number | null;
    rttMean: number;
    rttp50: number;
    rttp90: number;
    rttErrorRate: number;
  };
};

const useSocketIOClientStore = create<SocketIOClientStore>(() => ({
  socket: null,
  connected: false,
  telemetry: {
    rttTotal: 0,
    rttSuccess: 0,
    rttFail: 0,
    rttLatest: null,
    rttMean: 0,
    rttp50: 0,
    rttp90: 0,
    rttErrorRate: 0,
  },
}));

/**
 * Connect to the SocketIO server.
 *
 * Should only be called once from the root of the app.
 */
export const useSocketIOConnect = () => {
  const { authInfo } = useAuth();

  useInitSocketIOTelementry();

  useEffect(() => {
    const prev = useSocketIOClientStore.getState().socket;
    if (prev) {
      socketIODisconnect(prev);
    }

    const socket = socketIOConnect({
      authToken: authInfo?.access_token,
      setConnected: connected => {
        useSocketIOClientStore.setState({ connected });
      },
    });

    useSocketIOClientStore.setState({ socket });
    return () => {
      socketIODisconnect(socket);
    };
  }, [authInfo?.access_token]);
};

const useInitSocketIOTelementry = () => {
  const maxPingHistory = 100;
  const recentPings = useRef<Array<{ ts: number; rtt?: number }>>([]);
  // Subscribe to socket identity only — telemetry writes must not re-bind this listener.
  const socket = useSocketIOClientStore(state => state.socket);

  useInterval(1000 * 10, () => {
    const { connected, socket: liveSocket } = useSocketIOClientStore.getState();
    if (!connected || !liveSocket) {
      return;
    }
    const ping = { ts: Date.now() };
    recentPings.current.push(ping);
    if (recentPings.current.length > maxPingHistory) {
      recentPings.current.shift();
    }
    liveSocket.volatile.emit('ping', ping);
  });

  useSocketEvent(
    { socket },
    'pong',
    useCallback((data: { ts: number }) => {
      const ping = recentPings.current.find(p => p.ts === data.ts);
      if (!ping) {
        console.warn('SocketIO: received pong for unknown ping', data);
        return;
      }
      ping.rtt = Date.now() - data.ts;
      recalcTelemetry();
    }, []),
  );

  const recalcTelemetry = () => {
    const telemetry = getSocketTelemetryData(recentPings.current);
    window.RX_SOCKET_TELEMETRY_LAST_RESULT = telemetry;
    // Profile network chip reads this; keep it out of useSocketIOClient so RTT
    // ticks do not re-render every table / queue subscriber.
    useSocketIOClientStore.setState({ telemetry });
  };

  useInterval(1000 * 10, recalcTelemetry);

  /*
  SocketIO RTT telemetry is off. The stated reason was that Mixpanel covered it, which is
  no longer true: Mixpanel is gone (FOOD-652) and its own RTT tracking had been commented
  out since 2025-04. The 5-minute interval that used to wrap this went too, since it was
  waking up to do nothing.

  Kept as a self-contained recipe, import included, so re-enabling is uncomment-and-go
  rather than reassembling it from parts:

  import { telemetrySend } from '@/utils/telemetry';

  useInterval(1000 * 300, () => {
    const telemetry = io.telemetry;
    if (!telemetry.rttTotal) {
      return;
    }
    telemetrySend({
      name: 'SocketIO:RTT',
      key: '' + recentPings.current[recentPings.current.length - 1].ts,
      value: telemetry.rttLatest ?? 0,
      properties: {
        transport: io.socket?.io?.engine?.transport?.name,
        ...telemetry,
      },
    });
  });
  */
};

export type SocketIOClientHandle = Pick<SocketIOClientStore, 'socket' | 'connected'>;

/**
 * Socket + connected flag only. Telemetry lives in a separate selector so RTT
 * sampling does not re-render queue/table subscribers or rebind socket listeners.
 */
export const useSocketIOClient = (): SocketIOClientHandle => {
  const socket = useSocketIOClientStore(state => state.socket);
  const connected = useSocketIOClientStore(state => state.connected);
  return useMemo(() => ({ socket, connected }), [socket, connected]);
};

export const useSocketOnConnect = (io: SocketIOClientHandle, callback: (socket: Socket) => void) => {
  useEffect(() => {
    if (io.connected && io.socket) {
      callback(io.socket);
    }
  }, [io.socket, io.connected, callback]);
};

export const useSocketEvent = (
  io: Pick<SocketIOClientHandle, 'socket'>,
  event: string,
  callback: (...args: any[]) => void,
) => {
  useEffect(() => {
    io.socket?.on(event, callback);
    return () => {
      io.socket?.off(event, callback);
    };
  }, [io.socket, event, callback]);
};

/**
 * Send `ident` to the SocketIO presence server.
 */
export const useSocketIOPresenceIdent = () => {
  const io = useSocketIOClient();
  const location = useLocation();

  const currentPath = normalizePresenceLocation(buildUrl(location.pathname, location.search));
  const currentPathRef = useRef(currentPath);
  useLayoutEffect(() => {
    currentPathRef.current = currentPath;
  }, [currentPath]);

  // On (re)connect, push immediately so the server does not wait on the debounce.
  // Reads the path from a ref so route changes do not re-run this effect: those
  // go through the debounced emit below instead of emitting twice.
  useSocketOnConnect(
    io,
    useCallback(socket => {
      lastPresenceIdentLocation = currentPathRef.current;
      socket.emit('presence:ident', { location: currentPathRef.current });
    }, []),
  );

  // Route changes emit through the shared debounced helper (RxTable's
  // replaceState filter updates bypass the router and call it directly).
  useEffect(() => {
    emitPresenceIdent(currentPath);
  }, [currentPath]);
};

type PresenceIdent = any;

export interface PresenceStateUser {
  socketId: string;
  addr: string;
  uid: string | undefined;
  connectedAt: Date;
  ident: PresenceIdent;
}

type SocketIOPresenceStore = {
  activeUsersById: Record<string, PresenceStateUser>;
  allActiveUsers: PresenceStateUser[];
};

const useSocketIOPresenceStore = create<SocketIOPresenceStore>(() => ({
  activeUsersById: {},
  allActiveUsers: [],
}));

let pendingPresenceClients: PresenceStateUser[] | null = null;
let presenceStateApplyTimer: ReturnType<typeof setTimeout> | null = null;

const commitPresenceState = (clients: PresenceStateUser[]) => {
  const activeUsersById = {} as Record<string, PresenceStateUser>;
  const normalizedClients = clients.map(client => {
    const location = normalizePresenceLocation(client.ident?.location);
    const normalized =
      location === (client.ident?.location ?? '')
        ? client
        : {
            ...client,
            ident: { ...client.ident, location },
          };
    if (normalized.uid) {
      // String keys so number userIds from table rows match socket uids.
      activeUsersById[String(normalized.uid)] = normalized;
    }
    return normalized;
  });

  // Broadcasts are frequent; skip store updates when every client's uid + page
  // location is unchanged so foods/table PatientID rows and the header viewer
  // chip do not re-evaluate on every heartbeat. Compare per client, not per uid:
  // one user can have several tabs on different pages.
  const presenceClientKeys = (list: PresenceStateUser[]) =>
    list.map(c => `${c.uid ?? ''}\0${c.ident?.location ?? ''}`).sort();
  const prevKeys = presenceClientKeys(useSocketIOPresenceStore.getState().allActiveUsers);
  const nextKeys = presenceClientKeys(normalizedClients);
  if (prevKeys.length === nextKeys.length && prevKeys.every((key, i) => key === nextKeys[i])) {
    return;
  }

  useSocketIOPresenceStore.setState({
    activeUsersById,
    allActiveUsers: normalizedClients,
  });
};

/** Coalesce rapid presence:state bursts (many clients navigating at once). */
const schedulePresenceStateCommit = (clients: PresenceStateUser[]) => {
  pendingPresenceClients = clients;
  if (presenceStateApplyTimer != null) {
    return;
  }
  presenceStateApplyTimer = setTimeout(() => {
    presenceStateApplyTimer = null;
    const batch = pendingPresenceClients;
    pendingPresenceClients = null;
    if (batch) {
      commitPresenceState(batch);
    }
  }, 250);
};

/**
 * Join the presence channel and keep shared active-user state in sync.
 *
 * Should only be called once from the root of the app.
 */
export const useSocketIOPresenceSubscribe = () => {
  const io = useSocketIOClient();

  useSocketOnConnect(
    io,
    useCallback(
      socket => {
        socket.emit('presence:join');
      },
      [io.socket],
    ),
  );

  useSocketEvent(
    io,
    'presence:state',
    useCallback((clients: PresenceStateUser[]) => {
      schedulePresenceStateCommit(clients);
    }, []),
  );
};

/**
 * Read the shared list of connected users from the SocketIO presence server.
 * Prefer {@link useSocketIOPresenceIsOnline} in per-row cells so a broadcast
 * only re-renders rows whose online bit actually changed.
 */
export const useSocketIOPresenceActiveUsers = () => {
  const isConnected = useSocketIOClientStore(state => state.connected);
  const activeUsersById = useSocketIOPresenceStore(state => state.activeUsersById);
  const allActiveUsers = useSocketIOPresenceStore(state => state.allActiveUsers);

  return {
    activeUsersById,
    allActiveUsers,
    isConnected,
  };
};

/** Whether one user id is currently present. Stable across unrelated presence churn. */
export const useSocketIOPresenceIsOnline = (userId: string | number | null | undefined) => {
  const isConnected = useSocketIOClientStore(state => state.connected);
  const uidKey = userId == null || userId === '' ? '' : String(userId);
  const isOnline = useSocketIOPresenceStore(state => (!uidKey ? false : !!state.activeUsersById[uidKey]));

  return { isOnline, isConnected };
};

/**
 * Select a derived slice of the shared presence store. Pass an equality function
 * when the selector allocates (e.g. filtered viewer lists) so table cells only
 * re-render when their slice actually changes.
 */
export const useSocketIOPresenceSelect = <T>(
  selector: (state: SocketIOPresenceStore) => T,
  equalityFn?: (a: T, b: T) => boolean,
): T => useStoreWithEqualityFn(useSocketIOPresenceStore, selector, equalityFn);

const getSocketTelemetryData = (recentPings: Array<{ ts: number; rtt?: number }>) => {
  if (recentPings.length === 0) {
    return {
      rttTotal: 0,
      rttSuccess: 0,
      rttFail: 0,
      rttLatest: null,
      rttMean: 0,
      rttp50: 0,
      rttp90: 0,
      rttErrorRate: 0,
    };
  }

  const successPings = recentPings.filter(p => !!p.rtt) as Array<{ ts: number; rtt: number }>;
  const failedPings = recentPings.filter(p => !p.rtt);
  const gtFiveSecPings = recentPings.filter(p => Date.now() - p.ts > 5 * 1000);

  return {
    rttTotal: recentPings.length,
    rttSuccess: successPings.length,
    rttFail: failedPings.length,
    rttLatest: gtFiveSecPings.length ? (gtFiveSecPings[gtFiveSecPings.length - 1].rtt ?? 0) : null,
    rttMean: successPings.length ? successPings.reduce((a, b) => a + (b.rtt ?? 0), 0) / successPings.length : 0,
    rttp50: successPings.length
      ? successPings.sort((a, b) => a.rtt - b.rtt)[Math.ceil(successPings.length * 0.5) - 1].rtt
      : 0,
    rttp90: successPings.length
      ? successPings.sort((a, b) => a.rtt - b.rtt)[Math.ceil(successPings.length * 0.9) - 1].rtt
      : 0,
    rttErrorRate: failedPings.length / recentPings.length,
  };
};

export const useSocketTelemetryData = () => {
  return useSocketIOClientStore(state => state.telemetry);
};
